/**
 * Subscribe to a CSS media query and re-render on match changes. Used by the
 * artifacts panel to decide between the side-by-side (wide) and overlay (narrow)
 * layouts. SSR/test-safe: returns `false` when `matchMedia` is unavailable.
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The dock threshold. We dock the panel side-by-side only at >= 1024px (`lg`),
 * NOT `md` (768px): the chat shell already spends a fixed 288px (`w-72`) on the
 * conversation sidebar, so at 768px a docked panel (min 320px) would crush the
 * transcript to ~160px. Below `lg`, the panel renders as an overlay instead.
 * (Per the 2026-06-20 codex review, finding #3.)
 */
export const ARTIFACT_WIDE_QUERY = '(min-width: 1024px)';

/**
 * The chat surface's own two-layout threshold (same `lg` breakpoint): >= this
 * the conversation panel is an in-flow, collapsible column; below it the panel
 * is a sheet over the chat surface. Kept equal to the artifact dock threshold
 * so the page never mixes a docked column with an overlay at one width.
 */
export const CHAT_WIDE_QUERY = ARTIFACT_WIDE_QUERY;
